import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartDiscountUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * @typedef {object} GiftVoucherMapping
 * @property {string} [code] - Minted Shopify GIFT discount code.
 * @property {string} [endsAt] - ISO timestamp when the discount expires.
 * @property {string} [cardNumber] - ProfitPlus cadeaubon number.
 * @property {string} [CardNumber] - Alternate casing from API payloads.
 * @property {number} [cardValue] - Amount applied from this voucher.
 * @property {number} [cardAppliedValue] - Amount applied on this cart (from PP remint).
 * @property {number} [amountApplied] - Alias for cardValue in some payloads.
 * @property {number} [amount] - Alias for cardValue in some payloads.
 */

/** Strips spaces and dashes from a cadeaubon code. @param {string} raw */
const normalizeVoucherCode = (raw) => raw.replace(/[\s-]/g, '').trim();

const VISITOR_KEY = 'pelicanVisitorId';
const BASKET_KEY = 'pelicanBasketId';
const CHECKOUT_GUARD_KEY = 'feestwinkel:gift-voucher-checkout';

/** True while the customer is heading to / on Shopify checkout — pause remint + expiry. */
function isGiftVoucherCheckoutGuarded() {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname.startsWith('/checkout')) return true;
  try {
    return sessionStorage.getItem(CHECKOUT_GUARD_KEY) === '1';
  } catch {
    return false;
  }
}

function armGiftVoucherCheckoutGuard() {
  try {
    sessionStorage.setItem(CHECKOUT_GUARD_KEY, '1');
  } catch {
    /* ignore */
  }
}

function disarmGiftVoucherCheckoutGuard() {
  try {
    sessionStorage.removeItem(CHECKOUT_GUARD_KEY);
  } catch {
    /* ignore */
  }
}

function setupGiftVoucherCheckoutGuard() {
  if (typeof document === 'undefined') return;
  if (document.body.classList.contains('template-cart')) {
    disarmGiftVoucherCheckoutGuard();
  }
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      if (
        event.target.closest(
          '#checkout, [name="checkout"], .cart__checkout-button, a[href*="/checkout"]'
        )
      ) {
        armGiftVoucherCheckoutGuard();
      }
    },
    true
  );
}

/** @type {ReturnType<typeof setTimeout> | null} */
let deferGiftReconcileFallbackTimer = null;
/** Wait for shadow sync before reconcile after cart line changes with active vouchers. */
let deferGiftReconcileUntilShadowSync = false;

function clearDeferGiftReconcileFallback() {
  if (typeof deferGiftReconcileFallbackTimer === 'number') {
    window.clearTimeout(deferGiftReconcileFallbackTimer);
    deferGiftReconcileFallbackTimer = null;
  }
}

function scheduleDeferGiftReconcileFallback() {
  clearDeferGiftReconcileFallback();
  deferGiftReconcileFallbackTimer = window.setTimeout(() => {
    deferGiftReconcileFallbackTimer = null;
    void finishDeferredGiftReconcile();
  }, 2500);
}

async function finishDeferredGiftReconcile() {
  if (!deferGiftReconcileUntilShadowSync) return;
  deferGiftReconcileUntilShadowSync = false;
  clearDeferGiftReconcileFallback();
  const host = document.querySelector('cart-gift-voucher-component');
  if (!(host instanceof CartGiftVoucherComponent)) return;
  await host.reconcileAfterShadowSync();
}

function shadowBasketUrl() {
  const fromTheme = Theme.routes?.shadow_basket_sync_url;
  if (typeof fromTheme === 'string' && fromTheme.trim()) return fromTheme.trim();
  return 'https://api.eu.peliqan.io/3268/shadow-basket-sync';
}

async function fetchPeliqanCart() {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', 'peliqan-shadow-cart');
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Failed to fetch Peliqan cart section: ${response.status}`);
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const jsonEl = doc.querySelector('[data-peliqan-shadow-cart]');
  if (!jsonEl?.textContent) throw new Error('Peliqan cart section JSON missing');
  return JSON.parse(jsonEl.textContent);
}

/**
 * @param {Record<string, unknown>} extra
 * @returns {Promise<Record<string, unknown>>}
 */
async function requestShadowBasketSync(extra) {
  const helper = window.peliqanRequestShadowBasketSync;
  if (typeof helper === 'function') {
    return helper(extra);
  }

  let visitorId = localStorage.getItem(VISITOR_KEY);
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    localStorage.setItem(VISITOR_KEY, visitorId);
  }

  const shopifyCart = await fetchPeliqanCart();
  const basketIdRaw = localStorage.getItem(BASKET_KEY);
  const basketId = basketIdRaw ? Number(basketIdRaw) : null;
  const action = typeof extra.action === 'string' && extra.action ? extra.action : 'VOUCHER';
  const { action: _ignored, changedLineItems, ...rest } = extra;

  const response = await fetch(shadowBasketUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      visitorId,
      basketId: Number.isFinite(basketId) ? basketId : null,
      action,
      locale: shopifyCart.locale,
      customerShopifyId: shopifyCart.customerShopifyId,
      shopifyCart,
      changedLineItems: Array.isArray(changedLineItems) ? changedLineItems : null,
      ...rest,
    }),
  });
  const result = /** @type {Record<string, unknown>} */ (await response.json().catch(() => ({})));
  if (result.basketId != null && String(result.basketId)) {
    localStorage.setItem(BASKET_KEY, String(result.basketId));
  }
  if (!response.ok) {
    return { status: 'error', ok: false, ...result, http: response.status };
  }
  return result;
}

/**
 * @param {unknown} result
 */
function isShadowVoucherCheckOk(result) {
  if (!result || typeof result !== 'object') return false;
  const sync = /** @type {Record<string, unknown>} */ (result);
  if (sync.status === 'error' || sync.status === 'voucher_error') return false;
  const errors = Array.isArray(sync.voucherErrors) ? sync.voucherErrors : [];
  if (errors.length > 0) return false;
  const check = /** @type {{ ok?: boolean } | undefined} */ (sync.voucherCheck);
  if (check && check.ok === false) return false;
  return true;
}

/**
 * @param {unknown} result
 */
function shadowVoucherCheckMessage(result) {
  if (!result || typeof result !== 'object') return '';
  const sync = /** @type {Record<string, unknown>} */ (result);
  const errors = Array.isArray(sync.voucherErrors) ? sync.voucherErrors : [];
  const first = errors[0];
  if (first && typeof first === 'object' && typeof first.message === 'string' && first.message.trim()) {
    return first.message;
  }
  if (typeof sync.error === 'string' && sync.error.trim()) return sync.error;
  return '';
}

/**
 * @param {unknown} result
 * @returns {GiftVoucherMapping[]}
 */
function giftDiscountMappingsFromResult(result) {
  if (!result || typeof result !== 'object') return [];
  const sync = /** @type {Record<string, unknown>} */ (result);
  const raw = sync.giftDiscounts;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && typeof row === 'object')
    .map((row) => /** @type {GiftVoucherMapping} */ (row));
}

/**
 * @param {string} code
 */
function isGiftDiscountCode(code) {
  const text = String(code || '').trim().toUpperCase();
  return text.startsWith('GIFT-') || text.startsWith('GIFT:');
}

/**
 * Short label for minted Shopify GIFT codes — never show the full internal string in the UI.
 * @param {string} code
 */
function formatGiftDiscountCodeForDisplay(code) {
  const text = String(code || '').trim();
  if (!isGiftDiscountCode(text)) return text;
  const parts = text.split('-');
  if (parts.length >= 5) {
    const cents = parts[parts.length - 2] ?? '';
    if (/^\d+$/.test(cents)) {
      const euros = (Number(cents) / 100).toFixed(2).replace('.', ',');
      return `GIFT · €${euros}`;
    }
  }
  if (text.length <= 14) return text;
  return `${text.slice(0, 6)}…${text.slice(-4)}`;
}

/** Parse a locale-formatted money string (e.g. "€29,95") to EUR. @param {string} text */
function parseLocaleMoneyEur(text) {
  const cleaned = String(text || '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

/** Shopify-allocated GIFT amounts from the cart subtotaal block (source of truth for display). */
function readGiftAllocatedAmountsFromDom() {
  /** @type {Map<string, number>} */
  const amounts = new Map();
  for (const item of document.querySelectorAll('.cart-discounts__item')) {
    const labelEl = item.querySelector('.cart-discounts__label');
    const valueEl = item.querySelector('.cart-discounts__value');
    if (!(labelEl instanceof HTMLElement) || !(valueEl instanceof HTMLElement)) continue;

    const dataCode = labelEl.dataset.giftDiscountCode;
    if (typeof dataCode === 'string' && isGiftDiscountCode(dataCode)) {
      const amount = parseLocaleMoneyEur(valueEl.textContent || '');
      if (amount != null) amounts.set(dataCode.toUpperCase(), amount);
      continue;
    }

    const labelText = labelEl.textContent?.replace(/\s+/g, ' ').trim() || '';
    const codeMatch = labelText.match(/GIFT[-:][^\s]+/i);
    if (!codeMatch) continue;
    const code = codeMatch[0].toUpperCase();
    const amount = parseLocaleMoneyEur(valueEl.textContent || '');
    if (amount != null) amounts.set(code, amount);
  }
  return amounts;
}

function readCartSubtotalEurFromDom() {
  const el = document.querySelector('[data-testid="cart-subtotal-value"]');
  if (!el) return null;
  return parseLocaleMoneyEur(el.textContent || '');
}

/**
 * @param {GiftVoucherMapping} row
 * @param {Map<string, number>} allocatedByCode
 * @param {number | null} cartSubtotalEur
 * @param {boolean} singleGiftVoucher
 */
function giftRowDisplayAmountEur(row, allocatedByCode, cartSubtotalEur, singleGiftVoucher) {
  const code = String(row.code || '').toUpperCase();
  const allocated = allocatedByCode.get(code);
  if (typeof allocated === 'number' && Number.isFinite(allocated)) return allocated;

  const faceCandidates = [row.cardAppliedValue, row.cardValue, row.amountApplied, row.amount];
  let face = NaN;
  for (const raw of faceCandidates) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n)) {
      face = n;
      break;
    }
  }
  if (!Number.isFinite(face)) return NaN;
  if (singleGiftVoucher && cartSubtotalEur != null) return Math.min(face, cartSubtotalEur);
  return face;
}

/**
 * @param {Record<string, unknown>} cart
 * @returns {Map<string, number>}
 */
function giftAllocatedAmountsFromCartJson(cart) {
  /** @type {Map<string, number>} */
  const amounts = new Map();
  const apps = /** @type {Array<{ title?: string; total_allocated_amount?: number }> | undefined} */ (
    cart.cart_level_discount_applications
  );
  if (!Array.isArray(apps)) return amounts;
  for (const app of apps) {
    const title = String(app?.title || '');
    if (!isGiftDiscountCode(title)) continue;
    const cents = app.total_allocated_amount;
    if (typeof cents !== 'number' || !Number.isFinite(cents)) continue;
    amounts.set(title.toUpperCase(), cents / 100);
  }
  return amounts;
}

/**
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {import('@shopify/events').CartSummary | null}
 */
function cartSummaryFromAjaxResponse(data) {
  if (!data) return null;
  const create = CartDiscountUpdateEvent.createCartFromAjaxResponse;
  if (typeof create !== 'function') {
    return /** @type {import('@shopify/events').CartSummary} */ (/** @type {unknown} */ (data));
  }
  return create(/** @type {import('@shopify/events').CartAjaxResponse} */ (data));
}

/** @param {Record<string, unknown> | null | undefined} resource */
function getAppliedDiscountCodesFromResource(resource) {
  const discountCodes = /** @type {Array<{ code?: string }> | undefined} */ (resource?.discount_codes);
  if (!Array.isArray(discountCodes)) return [];
  return discountCodes
    .map((discount) => discount?.code)
    .filter((code) => typeof code === 'string' && code.length > 0)
    .map((code) => /** @type {string} */ (code));
}

/**
 * @param {import('@shopify/events').CartSummary | Record<string, unknown> | null | undefined} cart
 * @param {import('@shopify/events').CartLinesUpdateResultDetail | Record<string, unknown> | undefined} [detail]
 */
function cartItemCountFromLinesUpdate(cart, detail) {
  if (cart && typeof cart.totalQuantity === 'number') return cart.totalQuantity;
  if (detail && typeof detail.itemCount === 'number') return detail.itemCount;
  const rawCart = /** @type {Record<string, unknown> | null | undefined} */ (cart);
  if (rawCart && typeof rawCart.item_count === 'number') return rawCart.item_count;
  return null;
}

/** @param {Record<string, unknown> | null | undefined} cart */
function cartHasGiftVouchersOnResource(cart) {
  if (!cart) return false;
  if (getAppliedDiscountCodesFromResource(cart).some((code) => isGiftDiscountCode(code))) return true;
  const attributes = /** @type {Record<string, string> | undefined} */ (cart.attributes);
  return (
    typeof attributes?.gift_voucher_codes_json === 'string' &&
    attributes.gift_voucher_codes_json.trim().length > 0
  );
}

/** @type {Promise<Record<string, unknown> | null> | null} */
let clearGiftVouchersPromise = null;

/**
 * Remove all GIFT discount codes and the gift_voucher_codes_json cart attribute.
 * Works without a mounted cart-gift-voucher-component (e.g. after the cart is emptied).
 *
 * @param {{
 *   eventTarget?: EventTarget;
 *   morphSectionId?: string;
 *   onMorph?: () => void;
 *   skipShadowSync?: boolean;
 * }} [options]
 * @returns {Promise<Record<string, unknown> | null>}
 */
async function clearGiftVouchersFromShopifyCart(options = {}) {
  if (clearGiftVouchersPromise) return clearGiftVouchersPromise;

  clearGiftVouchersPromise = (async () => {
    /** @type {Record<string, unknown> | null} */
    let cart = null;
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartRes.ok) return null;
      cart = await cartRes.json();
    } catch {
      return null;
    }
    if (!cart) return null;

    const existing = getAppliedDiscountCodesFromResource(cart);
    const hasGiftDiscount = existing.some((code) => isGiftDiscountCode(code));
    const attributes = /** @type {Record<string, string> | undefined} */ (cart.attributes);
    const hasGiftAttribute =
      typeof attributes?.gift_voucher_codes_json === 'string' &&
      attributes.gift_voucher_codes_json.trim().length > 0;
    if (!hasGiftDiscount && !hasGiftAttribute) return null;

    const nextCodes = existing.filter((code) => !isGiftDiscountCode(code));
    /** @type {Record<string, unknown>} */
    const body = { attributes: { gift_voucher_codes_json: '' } };
    if (hasGiftDiscount) body.discount = nextCodes.join(',');
    if (options.morphSectionId) body.sections = [options.morphSectionId];

    const deferredPromise = CartDiscountUpdateEvent.createPromise();
    const eventTarget = options.eventTarget ?? document;
    eventTarget.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: nextCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
      });
      if (!response.ok) {
        deferredPromise.reject(new Error('cart update failed'));
        return null;
      }
      const data = /** @type {Record<string, unknown>} */ (await response.json());
      deferredPromise.resolve({ cart: cartSummaryFromAjaxResponse(data) });

      for (const el of document.querySelectorAll('cart-gift-voucher-component')) {
        if (el instanceof HTMLElement) delete el.dataset.cachedGiftCodesJson;
      }

      const sections = /** @type {Record<string, string> | undefined} */ (data.sections);
      const sectionHtml = options.morphSectionId ? sections?.[options.morphSectionId] : undefined;
      if (options.morphSectionId && sectionHtml) {
        const host =
          options.eventTarget instanceof CartGiftVoucherComponent ? options.eventTarget : null;
        morphSection(options.morphSectionId, sectionHtml, {
          mode: host?.closest('theme-drawer') ? 'hydration' : 'full',
        });
        options.onMorph?.();
      }

      if (!options.skipShadowSync) {
        try {
          await requestShadowBasketSync({ action: 'VOUCHER', voucherCodes: [] });
        } catch {
          /* non-blocking */
        }
      }

      return data;
    } catch (error) {
      deferredPromise.reject(error);
      throw error;
    }
  })().finally(() => {
    clearGiftVouchersPromise = null;
  });

  return clearGiftVouchersPromise;
}

class CartGiftVoucherComponent extends HTMLElement {
  /** @type {number | null} */
  #setupRafId = null;
  /** @type {number | null} */
  #timerIntervalId = null;
  /** @type {Record<string, string>} */
  #i18n = {};
  /** @type {boolean} */
  #i18nReady = false;
  /** @type {string} */
  #helperMessage = '';
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {AbortController | null} */
  #verifyAbort = null;
  /** @type {boolean} */
  #expiryCleanupStarted = false;
  /** Skip reconcile while we are applying/removing ourselves (avoids wiping attributes mid-flight). */
  #suppressReconcile = false;
  /** Avoid overlapping remint applies from shadow sync + remove flows. */
  #remintFromSyncInProgress = false;
  /** Drop stale async gift-list renders when qty / morph updates overlap. */
  #giftListRenderGeneration = 0;

  /** @returns {boolean} */
  #hasActiveGiftMappings() {
    return this.#readGiftMappings().length > 0;
  }

  async reconcileAfterShadowSync() {
    await this.#reconcileGiftMappingsWithAppliedCodes();
    this.#syncUiAfterSectionUpdate();
  }

  /** @returns {Record<string, string>} */
  #getI18n() {
    if (this.#i18nReady) return this.#i18n;
    const el = this.querySelector('script.cart-gift-voucher-i18n[type="application/json"]');
    if (!el?.textContent?.trim()) {
      this.#i18nReady = true;
      return this.#i18n;
    }
    try {
      const parsed = JSON.parse(/** @type {string} */ (el.textContent));
      this.#i18n =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? /** @type {Record<string, string>} */ (parsed)
          : {};
    } catch {
      this.#i18n = {};
    }
    this.#i18nReady = true;
    return this.#i18n;
  }

  /**
   * @param {string} key
   * @param {string} fallback
   */
  #t(key, fallback) {
    const value = this.#getI18n()[key];
    return typeof value === 'string' && value.length > 0 ? value : fallback;
  }

  /**
   * @param {string} tmpl
   * @param {number} minutes
   * @param {number} seconds
   */
  #formatTimerTemplate(tmpl, minutes, seconds) {
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
    return tmpl.replace(/__M__/g, pad(minutes)).replace(/__S__/g, pad(seconds));
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.#setup();
  }

  disconnectedCallback() {
    if (typeof this.#setupRafId === 'number') {
      window.cancelAnimationFrame(this.#setupRafId);
      this.#setupRafId = null;
    }
    this.removeEventListener('click', this.#onComponentClick);
    this.removeEventListener('keydown', this.#onGiftKeyDown);
    this.removeEventListener('input', this.#onGiftFieldInputDelegated);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#onDiscountUpdate);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
    if (typeof this.#timerIntervalId === 'number') {
      window.clearInterval(this.#timerIntervalId);
      this.#timerIntervalId = null;
    }
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#verifyAbort?.abort();
    this.#verifyAbort = null;
    this.#expiryCleanupStarted = false;
    delete this.dataset.initialized;
  }

  #setup = () => {
    if (!this.isConnected || this.dataset.initialized === 'true') return;

    const button = this.querySelector('[data-gift-validate]');
    const cardInput = this.querySelector('[data-gift-card]');
    const message = this.querySelector('[data-gift-message]');

    if (
      !(button instanceof HTMLButtonElement) ||
      !(cardInput instanceof HTMLInputElement) ||
      !(message instanceof HTMLElement)
    ) {
      this.#setupRafId = window.requestAnimationFrame(this.#setup);
      return;
    }

    this.dataset.initialized = 'true';
    this.#setupRafId = null;
    this.#helperMessage = this.#t(
      'helper',
      message.textContent?.trim() || 'Enter your gift voucher code.'
    );
    this.addEventListener('click', this.#onComponentClick);
    this.addEventListener('keydown', this.#onGiftKeyDown);
    this.addEventListener('input', this.#onGiftFieldInputDelegated);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onDiscountUpdate);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onLinesUpdate);
    this.#startTimer();
    this.#updateRemoveButtonVisibility();
    this.#renderGiftList();
    this.#syncHelperMessage();
    void this.#reconcileGiftMappingsWithAppliedCodes();
  };

  #onGiftFieldInput = () => {
    const message = this.querySelector('[data-gift-message]');
    if (message instanceof HTMLElement && message.dataset.state) {
      this.#setMessage('', '');
    }
    this.#syncHelperMessage();
  };

  #onGiftFieldInputDelegated = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('[data-gift-card]')) return;
    this.#onGiftFieldInput();
  };

  #onGiftKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Enter') return;
    if (!(event.target instanceof Element) || !event.target.closest('[data-gift-card]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void this.#onValidate();
  };

  /** @param {Event} event */
  #onComponentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-gift-validate]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void this.#onValidate();
      return;
    }
    const one = event.target.closest('[data-gift-remove-one]');
    if (one instanceof HTMLElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const code = one.dataset.giftCode || '';
      const card = one.dataset.giftCardNumber || '';
      void this.#removeOneGiftVoucher({ code, cardNumber: card });
      return;
    }
    if (event.target.closest('[data-gift-remove]')) {
      void this.#removeAllGiftVouchers();
    }
  };

  /**
   * @param {string} [code]
   */
  submitVoucher(code) {
    const cardInput = this.querySelector('[data-gift-card]');
    if (typeof code === 'string' && code && cardInput instanceof HTMLInputElement) {
      cardInput.value = code;
    }
    return this.#onValidate();
  }

  #syncHelperMessage() {
    const cardInput = this.querySelector('[data-gift-card]');
    const message = this.querySelector('[data-gift-message]');
    if (!(cardInput instanceof HTMLInputElement) || !(message instanceof HTMLElement)) return;

    const hasGiftCodes = (this.dataset.cachedGiftCodesJson?.trim() ?? '').length > 0;
    if (hasGiftCodes) {
      if (message.dataset.state !== 'error') {
        this.#setMessage(this.#t('applied', 'Gift voucher applied.'), 'success');
      }
      return;
    }

    if (message.dataset.state) return;

    if (normalizeVoucherCode(cardInput.value).length > 0) {
      this.#setMessage('', '');
      return;
    }

    if (this.#helperMessage) this.#setMessage(this.#helperMessage, '');
  }

  #syncUiAfterSectionUpdate() {
    this.#updateRemoveButtonVisibility();
    this.#renderGiftList();
    this.#startTimer();
    this.#syncHelperMessage();
  }

  /** Wait for cart section morph (drawer hydration) before rebuilding client-rendered gift UI. */
  #scheduleUiSyncAfterMorph() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#syncUiAfterSectionUpdate());
    });
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onLinesUpdate = async (event) => {
    await event.promise
      ?.then(async ({ cart, detail }) => {
        const itemCount = cartItemCountFromLinesUpdate(
          /** @type {Record<string, unknown> | undefined} */ (cart),
          detail
        );
        if (itemCount !== null && itemCount <= 0) {
          deferGiftReconcileUntilShadowSync = false;
          clearDeferGiftReconcileFallback();
          await clearGiftVouchersFromShopifyCart({
            eventTarget: this,
            morphSectionId: this.dataset.sectionId || undefined,
            onMorph: () => this.#syncUiAfterSectionUpdate(),
          });
          return;
        }

        const hadGiftVouchers =
          this.#hasActiveGiftMappings() ||
          cartHasGiftVouchersOnResource(/** @type {Record<string, unknown> | undefined} */ (cart));
        if (hadGiftVouchers) {
          deferGiftReconcileUntilShadowSync = false;
          clearDeferGiftReconcileFallback();
          await clearGiftVouchersFromShopifyCart({
            eventTarget: this,
            morphSectionId: this.dataset.sectionId || undefined,
            onMorph: () => this.#syncUiAfterSectionUpdate(),
          });
          this.#stopTimer();
          this.#setMessage(
            this.#t(
              'cartChanged',
              'Your cart changed. Gift vouchers were removed — apply them again when you are done shopping.'
            ),
            'info'
          );
          this.#updateRemoveButtonVisibility();
          this.#renderGiftList();
          return;
        }

        await this.#reconcileGiftMappingsWithAppliedCodes(
          /** @type {Record<string, unknown> | undefined} */ (cart)
        );
        this.#scheduleUiSyncAfterMorph();
      })
      .catch(() => {});
  };

  /** @param {CartDiscountUpdateEvent} event */
  #onDiscountUpdate = async (event) => {
    this.#scheduleUiSyncAfterMorph();
    await event.promise
      ?.then(async ({ detail }) => {
        const cart = /** @type {Record<string, unknown> | undefined} */ (detail?.cart);
        await this.#reconcileGiftMappingsWithAppliedCodes(cart);
        this.#syncUiAfterSectionUpdate();
      })
      .catch(() => {});
  };

  /** @returns {GiftVoucherMapping[]} */
  #readGiftMappings() {
    const raw = this.dataset.cachedGiftCodesJson?.trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? /** @type {GiftVoucherMapping[]} */ (parsed) : [];
    } catch {
      return [];
    }
  }

  /** @param {Record<string, unknown> | null | undefined} resource */
  #getAppliedDiscountCodes(resource) {
    return getAppliedDiscountCodesFromResource(resource);
  }

  /**
   * GIFT codes that are actually reducing the cart (applicable === true).
   * @param {Record<string, unknown> | null | undefined} resource
   */
  #getApplicableGiftCodes(resource) {
    const discountCodes = /** @type {Array<{ code?: string; applicable?: boolean }> | undefined} */ (
      resource?.discount_codes
    );
    if (!Array.isArray(discountCodes)) return [];
    return discountCodes
      .filter(
        (row) =>
          typeof row?.code === 'string' &&
          row.code.length > 0 &&
          row.applicable === true &&
          isGiftDiscountCode(row.code)
      )
      .map((row) => /** @type {string} */ (row.code));
  }

  /** Hide timer + clear interval so it cannot linger after remove/reconcile. */
  #stopTimer() {
    if (typeof this.#timerIntervalId === 'number') {
      window.clearInterval(this.#timerIntervalId);
      this.#timerIntervalId = null;
    }
    const timerNode = this.querySelector('[data-gift-timer]');
    if (timerNode instanceof HTMLElement) {
      timerNode.classList.add('hidden');
      timerNode.textContent = '';
    }
  }

  /** @param {Record<string, unknown>} [resource] */
  async #reconcileGiftMappingsWithAppliedCodes(resource) {
    if (this.#suppressReconcile) return;

    const mappings = this.#readGiftMappings();
    if (mappings.length === 0) {
      this.#stopTimer();
      this.#renderGiftList();
      this.#updateRemoveButtonVisibility();
      return;
    }

    /** @type {Record<string, unknown> | null | undefined} */
    let cart = resource;
    let applicableGift = this.#getApplicableGiftCodes(cart);
    // Only refetch when we have no cart payload yet.
    if (!cart) {
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartRes.ok) {
          cart = await cartRes.json();
          applicableGift = this.#getApplicableGiftCodes(cart);
        }
      } catch {
        return;
      }
    } else if (applicableGift.length === 0 && !Array.isArray(cart.discount_codes)) {
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartRes.ok) {
          cart = await cartRes.json();
          applicableGift = this.#getApplicableGiftCodes(cart);
        }
      } catch {
        return;
      }
    }

    const appliedSet = new Set(applicableGift.map((code) => code.toUpperCase()));
    const nextMappings = mappings.filter((mapping) => {
      const code = mapping?.code;
      if (typeof code !== 'string' || !code) return false;
      return appliedSet.has(code.toUpperCase());
    });
    if (nextMappings.length === mappings.length) {
      this.#renderGiftList();
      this.#startTimer();
      this.#updateRemoveButtonVisibility();
      return;
    }

    if (nextMappings.length === 0) {
      this.#suppressReconcile = true;
      try {
        delete this.dataset.cachedGiftCodesJson;
        await clearGiftVouchersFromShopifyCart({
          eventTarget: this,
          morphSectionId: this.dataset.sectionId || undefined,
          onMorph: () => this.#syncUiAfterSectionUpdate(),
        });
      } finally {
        this.#suppressReconcile = false;
      }
      this.#stopTimer();
      this.#renderGiftList();
      this.#updateRemoveButtonVisibility();
      this.#syncHelperMessage();
      return;
    }

    const nextJson = JSON.stringify(nextMappings);
    if (nextJson) this.dataset.cachedGiftCodesJson = nextJson;

    await this.#updateCart({ attributes: { gift_voucher_codes_json: nextJson } });

    const remainingCards = nextMappings
      .map((row) => normalizeVoucherCode(String(row?.cardNumber || row?.CardNumber || '')))
      .filter(Boolean);
    try {
      await requestShadowBasketSync({ action: 'VOUCHER', voucherCodes: remainingCards });
    } catch {
      /* non-blocking */
    }

    this.#startTimer();
    this.#renderGiftList();
    this.#updateRemoveButtonVisibility();
  }

  /** Render one row per applied cadeaubon (own GIFT code + remove). */
  #renderGiftList() {
    void this.#renderGiftListWithAmounts();
  }

  /** Render gift rows using Shopify-allocated amounts, not stale mint face values. */
  async #renderGiftListWithAmounts() {
    const list = this.querySelector('[data-gift-list]');
    if (!(list instanceof HTMLElement)) return;

    const generation = ++this.#giftListRenderGeneration;

    const mappings = this.#readGiftMappings().filter((row) => {
      const code = typeof row?.code === 'string' ? row.code : '';
      return code && isGiftDiscountCode(code);
    });

    // Deduplicate by Shopify discount code (one row per GIFT).
    const seen = new Set();
    /** @type {GiftVoucherMapping[]} */
    const unique = [];
    for (const row of mappings) {
      const code = String(row.code || '').toUpperCase();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      unique.push(row);
    }

    let allocatedByCode = readGiftAllocatedAmountsFromDom();
    const needsCartFetch = unique.some((row) => !allocatedByCode.has(String(row.code || '').toUpperCase()));
    if (needsCartFetch) {
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartRes.ok) {
          const cart = /** @type {Record<string, unknown>} */ (await cartRes.json());
          if (generation !== this.#giftListRenderGeneration) return;
          allocatedByCode = giftAllocatedAmountsFromCartJson(cart);
        }
      } catch {
        /* keep DOM fallback */
      }
    }

    if (generation !== this.#giftListRenderGeneration) return;

    const cartSubtotalEur = readCartSubtotalEurFromDom();
    const singleGiftVoucher = unique.length === 1;
    const moneyFmt = new Intl.NumberFormat(document.documentElement.lang || 'nl', {
      style: 'currency',
      currency: 'EUR',
    });

    list.replaceChildren();
    const removeLabel = this.#t('removeOne', 'Remove');
    for (const row of unique) {
      const code = String(row.code || '');
      const card = normalizeVoucherCode(String(row.cardNumber || row.CardNumber || ''));
      const displayLabel = card || this.#t('label', 'Gift voucher');
      const amount = giftRowDisplayAmountEur(row, allocatedByCode, cartSubtotalEur, singleGiftVoucher);
      const amountLabel = Number.isFinite(amount) ? moneyFmt.format(amount) : '';

      const li = document.createElement('li');
      li.className = 'cart-gift-voucher__item';

      const meta = document.createElement('div');
      meta.className = 'cart-gift-voucher__item-meta';

      const codeEl = document.createElement('span');
      codeEl.className = 'cart-gift-voucher__item-code cart-primary-typography';
      codeEl.textContent = displayLabel;
      if (card && code) codeEl.title = formatGiftDiscountCodeForDisplay(code);
      meta.appendChild(codeEl);

      if (amountLabel) {
        const amountEl = document.createElement('span');
        amountEl.className = 'cart-gift-voucher__item-amount cart-primary-typography';
        amountEl.textContent = `−${amountLabel}`;
        meta.appendChild(amountEl);
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cart-gift-voucher__item-remove cart-primary-typography';
      btn.dataset.giftRemoveOne = '';
      btn.dataset.giftCode = code;
      if (card) btn.dataset.giftCardNumber = card;
      btn.setAttribute('aria-label', `${removeLabel}: ${code}`);
      btn.textContent = removeLabel;

      li.appendChild(meta);
      li.appendChild(btn);
      list.appendChild(li);
    }
  }

  /**
   * Remove a single GIFT code / cadeaubon; keep the others.
   * @param {{ code?: string; cardNumber?: string }} target
   */
  async #removeOneGiftVoucher(target) {
    const codeWanted = String(target?.code || '').trim().toUpperCase();
    const cardWanted = normalizeVoucherCode(target?.cardNumber || '').toUpperCase();
    if (!codeWanted && !cardWanted) return;

    const mappings = this.#readGiftMappings();
    const remaining = mappings.filter((row) => {
      const code = String(row?.code || '').trim().toUpperCase();
      const card = normalizeVoucherCode(String(row?.cardNumber || row?.CardNumber || '')).toUpperCase();
      if (codeWanted && code === codeWanted) return false;
      if (!codeWanted && cardWanted && card === cardWanted) return false;
      return true;
    });

    if (remaining.length === mappings.length) return;

    if (remaining.length === 0) {
      await this.#removeAllGiftVouchers();
      return;
    }

    this.#setMessage(this.#t('removing', 'Removing gift voucher…'), '');
    this.#suppressReconcile = true;
    try {
      const remainingGiftCodes = [
        ...new Set(
          remaining
            .map((row) => String(row?.code || '').trim())
            .filter((code) => code && isGiftDiscountCode(code))
        ),
      ];
      const remainingCards = [
        ...new Set(
          remaining
            .map((row) => normalizeVoucherCode(String(row?.cardNumber || row?.CardNumber || '')))
            .filter(Boolean)
        ),
      ];

      /** @type {string[]} */
      let existing = [];
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (cartRes.ok) existing = this.#getAppliedDiscountCodes(await cartRes.json());
      } catch {
        existing = [];
      }
      const keptNonGift = existing.filter((code) => !isGiftDiscountCode(code));
      const nextCodes = [...keptNonGift, ...remainingGiftCodes];
      const codesJson = JSON.stringify(remaining);

      const deferredPromise = CartDiscountUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: nextCodes.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      const data = await this.#updateCart({
        discount: nextCodes.join(','),
        attributes: { gift_voucher_codes_json: codesJson },
      });
      deferredPromise.resolve({ cart: data });
      if (!data) throw new Error('cart update failed');

      const syncResult = await requestShadowBasketSync({
        action: 'VOUCHER',
        voucherCodes: remainingCards,
      });

      const reminted = giftDiscountMappingsFromResult(syncResult).map((row) => ({
        ...row,
        cardNumber: row.cardNumber || row.CardNumber,
      }));
      if (isShadowVoucherCheckOk(syncResult) && reminted.length > 0) {
        await this.applyRemintedMappings(reminted, { silent: true });
      } else {
        this.dataset.cachedGiftCodesJson = codesJson;
        this.#renderGiftList();
        this.#startTimer();
        this.#updateRemoveButtonVisibility();
      }

      this.#setMessage(this.#t('removed', 'Gift voucher removed.'), 'success');
    } catch {
      this.#setMessage(this.#t('removeFailed', 'Could not remove gift voucher. Please try again.'), 'error');
    } finally {
      this.#suppressReconcile = false;
    }
  }

  /**
   * True when the cart's applicable GIFT set exactly matches wantedCodes.
   * @param {Record<string, unknown> | null | undefined} cart
   * @param {string[]} wantedCodes
   */
  #giftCodeSetMatches(cart, wantedCodes) {
    const current = this.#getApplicableGiftCodes(cart)
      .map((code) => code.toUpperCase())
      .sort();
    const wanted = wantedCodes.map((code) => String(code || '').toUpperCase()).filter(Boolean).sort();
    return current.length === wanted.length && current.every((code, idx) => code === wanted[idx]);
  }

  /**
   * @param {GiftVoucherMapping[]} current
   * @param {GiftVoucherMapping[]} reminted
   */
  #giftMappingsMatchReminted(current, reminted) {
    const normalize = (/** @type {GiftVoucherMapping[]} */ rows) =>
      JSON.stringify(
        rows
          .map((row) => ({
            code: String(row.code || '').toUpperCase(),
            card: normalizeVoucherCode(String(row.cardNumber || row.CardNumber || '')).toUpperCase(),
            amount: Number(row.cardAppliedValue ?? row.cardValue ?? row.amount) || 0,
          }))
          .sort((a, b) => a.code.localeCompare(b.code))
      );
    return normalize(current) === normalize(reminted);
  }

  /**
   * Swap in freshly minted GIFT codes (cart line/qty changes or partial remove).
   * @param {GiftVoucherMapping[]} reminted
   * @param {{ silent?: boolean }} [options]
   */
  async applyRemintedMappings(reminted, { silent = false } = {}) {
    if (this.#suppressReconcile || this.#remintFromSyncInProgress) return false;
    if (isGiftVoucherCheckoutGuarded()) return false;

    const finalMappings = reminted.map((row) => ({
      ...row,
      cardNumber: row.cardNumber || row.CardNumber,
    }));
    if (finalMappings.length === 0) return false;

    const remintCodes = [
      ...new Set(
        finalMappings
          .map((row) => (typeof row.code === 'string' ? row.code : ''))
          .filter((code) => code && isGiftDiscountCode(code))
      ),
    ];
    if (remintCodes.length === 0) return false;

    /** @type {Record<string, unknown> | null} */
    let cart = null;
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartRes.ok) cart = await cartRes.json();
    } catch {
      cart = null;
    }

    const currentMappings = this.#readGiftMappings();
    if (
      this.#giftCodeSetMatches(cart, remintCodes) &&
      this.#giftMappingsMatchReminted(currentMappings, finalMappings)
    ) {
      return false;
    }

    this.#remintFromSyncInProgress = true;
    this.#suppressReconcile = true;
    try {
      const refreshed = await this.#applyGiftDiscountCodes(remintCodes);
      if (!refreshed) return false;

      const finalCodesJson = JSON.stringify(finalMappings);
      await this.#updateCart({ attributes: { gift_voucher_codes_json: finalCodesJson } });
      this.dataset.cachedGiftCodesJson = finalCodesJson;
      this.#renderGiftList();
      this.#startTimer();
      this.#updateRemoveButtonVisibility();
      if (!silent) {
        this.#setMessage(this.#t('applied', 'Gift voucher applied.'), 'success');
      }
      return true;
    } finally {
      this.#suppressReconcile = false;
      this.#remintFromSyncInProgress = false;
    }
  }

  /**
   * Keep non-GIFT discount codes; replace previous GIFT codes with the minted ones.
   * Discount apply is a dedicated cart/update (same as cart-discount.js). Attributes
   * are saved only after the code is confirmed applicable.
   * @param {string[]} giftCodes
   */
  async #applyGiftDiscountCodes(giftCodes) {
    const uniqueGift = [
      ...new Set(
        giftCodes
          .map((code) => String(code || '').trim())
          .filter((code) => code && isGiftDiscountCode(code))
      ),
    ];
    if (uniqueGift.length === 0) return null;

    let existingCart = /** @type {Record<string, unknown> | null} */ (null);
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartRes.ok) existingCart = await cartRes.json();
    } catch {
      existingCart = null;
    }

    const existing = this.#getAppliedDiscountCodes(existingCart);
    // Exact set match only — do not early-return while stale GIFT codes remain.
    if (this.#giftCodeSetMatches(existingCart, uniqueGift)) {
      return existingCart;
    }

    const kept = existing.filter((code) => !isGiftDiscountCode(code));
    const nextCodes = [...kept, ...uniqueGift];

    const deferredPromise = CartDiscountUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: nextCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      /** @type {Record<string, unknown> | null} */
      let data = null;
      // Newly minted codes can be briefly non-applicable; retry a few times.
      for (let attempt = 0; attempt < 4; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
        }

        if (attempt === 1) {
          for (const code of uniqueGift) {
            try {
              await fetch(`/discount/${encodeURIComponent(code)}`, {
                method: 'GET',
                credentials: 'same-origin',
                redirect: 'manual',
              });
            } catch {
              /* continue */
            }
          }
        }

        data = await this.#postCartUpdate({ discount: nextCodes.join(',') }, { morph: false });
        if (data && this.#giftCodeSetMatches(data, uniqueGift)) {
          data = (await this.#updateCart({ discount: nextCodes.join(',') })) || data;
          deferredPromise.resolve({
            cart: cartSummaryFromAjaxResponse(data),
          });
          return data;
        }
      }

      deferredPromise.resolve({
        cart: cartSummaryFromAjaxResponse(data),
      });
      return null;
    } catch (error) {
      deferredPromise.reject(error);
      throw error;
    }
  }

  /** Strip GIFT discount codes from the cart while keeping other codes. */
  async #clearGiftDiscountCodes() {
    return clearGiftVouchersFromShopifyCart({
      eventTarget: this,
      morphSectionId: this.dataset.sectionId || undefined,
      onMorph: () => this.#syncUiAfterSectionUpdate(),
      skipShadowSync: true,
    });
  }

  #startTimer() {
    const timerNode = this.querySelector('[data-gift-timer]');
    if (!(timerNode instanceof HTMLElement)) return;

    const raw = this.dataset.cachedGiftCodesJson?.trim();
    if (!raw) {
      this.#stopTimer();
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.#stopTimer();
      return;
    }

    const mappings = Array.isArray(parsed) ? parsed : [];
    const endsAts = mappings
      .map((item) => {
        if (!item || typeof item !== 'object' || !('endsAt' in item)) return NaN;
        const endsAtStr = /** @type {{ endsAt?: string }} */ (item).endsAt;
        if (typeof endsAtStr !== 'string' || !endsAtStr) return NaN;
        const stamp = new Date(endsAtStr).getTime();
        return Number.isNaN(stamp) ? NaN : stamp;
      })
      .filter((stamp) => !Number.isNaN(stamp));

    if (endsAts.length === 0) {
      this.#stopTimer();
      return;
    }

    const endsAt = Math.min(...endsAts);
    const tick = () => {
      if (isGiftVoucherCheckoutGuarded()) return;
      const remainingMs = endsAt - Date.now();
      if (remainingMs <= 0) {
        timerNode.textContent = this.#t('timerExpired', 'Gift discount expired.');
        timerNode.classList.remove('hidden');
        if (typeof this.#timerIntervalId === 'number') {
          window.clearInterval(this.#timerIntervalId);
          this.#timerIntervalId = null;
        }
        if (!this.#expiryCleanupStarted) {
          this.#expiryCleanupStarted = true;
          void this.#removeAllGiftVouchers({ expired: true }).finally(() => {
            this.#expiryCleanupStarted = false;
          });
        }
        return;
      }
      const minutes = Math.floor(remainingMs / 60000);
      const seconds = Math.floor((remainingMs % 60000) / 1000);
      timerNode.textContent = this.#formatTimerTemplate(
        this.#t('timerRunning', 'Gift discount expires in __M__:__S__'),
        minutes,
        seconds
      );
      timerNode.classList.remove('hidden');
    };

    if (typeof this.#timerIntervalId === 'number') window.clearInterval(this.#timerIntervalId);
    tick();
    this.#timerIntervalId = window.setInterval(tick, 1000);
  }

  /**
   * @param {string} text
   * @param {'error' | 'success' | 'info' | ''} [kind]
   */
  #setMessage(text, kind = '') {
    const message = this.querySelector('[data-gift-message]');
    if (!(message instanceof HTMLElement)) return;
    message.textContent = text;
    message.dataset.state = kind;
    message.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    if (text) {
      message.hidden = false;
      message.removeAttribute('hidden');
    } else {
      message.hidden = true;
      message.setAttribute('hidden', '');
    }
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    const button = this.querySelector('[data-gift-validate]');
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = loading;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  /** @returns {Promise<boolean>} */
  async #isCartFullyCovered() {
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartRes.ok) return false;
      const cart = await cartRes.json();
      const itemCount = Number(cart?.item_count ?? 0);
      const totalPrice = Number(cart?.total_price ?? NaN);
      return itemCount > 0 && Number.isFinite(totalPrice) && totalPrice <= 0;
    } catch {
      return false;
    }
  }

  #onValidate = async () => {
    this.#setMessage('', '');
    const cartItemCount = Number(this.dataset.cartItemCount || '0');
    if (cartItemCount <= 0) {
      this.#setMessage(this.#t('emptyCart', 'Add items to your cart to use a gift voucher.'), 'error');
      return;
    }

    const cardInput = this.querySelector('[data-gift-card]');
    if (!(cardInput instanceof HTMLInputElement)) return;
    const voucherCode = normalizeVoucherCode(cardInput.value);
    if (!voucherCode) {
      this.#setMessage(this.#t('enterCard', 'Enter your gift voucher code.'), 'error');
      return;
    }

    if (await this.#isCartFullyCovered()) {
      this.#setMessage(
        this.#t('fullRedemptionRequired', 'Your cart balance is already covered.'),
        'error'
      );
      return;
    }

    // No stored basket ID is fine: the sync sends basketId null and Peliqan creates the shadow basket.
    this.#setLoading(true);
    this.#suppressReconcile = true;
    try {
      this.#setMessage(this.#t('checkingBasket', 'Checking gift voucher on the basket…'), '');

      const existingCards = this.#readGiftMappings()
        .map((row) => normalizeVoucherCode(String(row?.cardNumber || row?.CardNumber || '')))
        .filter(Boolean);
      const voucherCodes = [...new Set([...existingCards, voucherCode])];

      const expiresRaw = Number(this.dataset.expirationMinutes || '20');
      const expiresInMinutes = Number.isFinite(expiresRaw) && expiresRaw > 0 ? expiresRaw : 20;

      const verifyResult = await requestShadowBasketSync({
        action: 'VOUCHER',
        voucherCodes,
        expiresInMinutes,
      });

      if (!isShadowVoucherCheckOk(verifyResult)) {
        this.#setMessage(
          shadowVoucherCheckMessage(verifyResult) ||
            this.#t('notApplied', 'Gift voucher was not applied correctly. It has been removed from the cart.'),
          'error'
        );
        return;
      }

      const giftMappings = giftDiscountMappingsFromResult(verifyResult).map((row) => ({
        ...row,
        cardNumber: row.cardNumber || row.CardNumber || voucherCode,
      }));
      if (giftMappings.length === 0) {
        this.#setMessage(
          this.#t('noCodeReturned', 'Could not create a gift discount code. Please try again.'),
          'error'
        );
        return;
      }

      const giftCodes = [
        ...new Set(
          giftMappings
            .map((row) => (typeof row.code === 'string' ? row.code : ''))
            .filter((code) => code && isGiftDiscountCode(code))
        ),
      ];
      if (giftCodes.length === 0) {
        this.#setMessage(
          this.#t('noCodeReturned', 'Could not create a gift discount code. Please try again.'),
          'error'
        );
        return;
      }

      // Server returned the full set for all cards — replace, don't merge stale codes.
      const codesJson = JSON.stringify(giftMappings);
      // Set before cart discount update so a late reconcile (if any) keeps these rows.
      this.dataset.cachedGiftCodesJson = codesJson;

      const discountCart = await this.#applyGiftDiscountCodes(giftCodes);
      if (!discountCart) {
        delete this.dataset.cachedGiftCodesJson;
        this.#setMessage(
          this.#t('createFailed', 'Could not apply the gift discount to your cart.'),
          'error'
        );
        return;
      }

      const updatedCart = await this.#updateCart({
        attributes: { gift_voucher_codes_json: codesJson },
      });
      if (!updatedCart) {
        this.#setMessage(this.#t('applyFailed', 'Could not apply gift voucher to cart.'), 'error');
        return;
      }

      this.#renderGiftList();
      this.#startTimer();
      this.#updateRemoveButtonVisibility();
      this.#setMessage(this.#t('applied', 'Gift voucher applied.'), 'success');
      if (cardInput) cardInput.value = '';
    } catch (error) {
      this.#setMessage(this.#t('genericError', 'Something went wrong. Please try again.'), 'error');
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to apply gift voucher',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#suppressReconcile = false;
      this.#setLoading(false);
    }
  };

  #updateRemoveButtonVisibility() {
    const removeBtn = this.querySelector('[data-gift-remove]');
    if (!(removeBtn instanceof HTMLElement)) return;
    const hasGiftCodes = (this.dataset.cachedGiftCodesJson?.trim() ?? '').length > 0;
    removeBtn.classList.toggle('hidden', !hasGiftCodes);
  }

  async #removeAllGiftVouchers({ expired = false, skipSuccessMessage = false } = {}) {
    const removeBtn = this.querySelector('[data-gift-remove]');
    if (removeBtn instanceof HTMLButtonElement) removeBtn.disabled = true;
    if (!expired && !skipSuccessMessage) {
      this.#setMessage(this.#t('removing', 'Removing gift voucher…'), '');
    }

    this.#suppressReconcile = true;
    try {
      await this.#clearGiftDiscountCodes();
      await requestShadowBasketSync({ action: 'VOUCHER', voucherCodes: [] });

      delete this.dataset.cachedGiftCodesJson;
      this.#stopTimer();
      this.#renderGiftList();
      this.#updateRemoveButtonVisibility();
      if (!skipSuccessMessage) {
        this.#setMessage(
          expired
            ? this.#t('timerExpired', 'Gift discount expired.')
            : this.#t('removed', 'Gift voucher removed.'),
          expired ? 'error' : 'success'
        );
      }
    } catch {
      this.#setMessage(this.#t('removeFailed', 'Could not remove gift voucher. Please try again.'), 'error');
    } finally {
      this.#suppressReconcile = false;
      if (removeBtn instanceof HTMLButtonElement) removeBtn.disabled = false;
    }
  }

  /** Public entry for cart-discount fallback if a GIFT pill is ever rendered. */
  removeAllGiftVouchers() {
    return this.#removeAllGiftVouchers();
  }

  /**
   * @param {Record<string, unknown>} payload
   * @param {{ morph?: boolean }} [options]
   */
  async #postCartUpdate(payload, options = {}) {
    const shouldMorph = options.morph !== false;
    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const sectionId = this.dataset.sectionId;
    const body = { ...payload };
    if (sectionId && shouldMorph) body.sections = [sectionId];

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
        signal: abortController.signal,
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();

      if (shouldMorph && sectionId && data.sections?.[sectionId]) {
        morphSection(sectionId, data.sections[sectionId], {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
        this.#syncUiAfterSectionUpdate();
      }
      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return null;
      throw error;
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /** @param {Record<string, unknown>} payload */
  async #updateCart(payload) {
    return this.#postCartUpdate(payload, { morph: true });
  }
}

function upgradeGiftVoucherComponents(root = document) {
  for (const el of root.querySelectorAll('cart-gift-voucher-component')) {
    customElements.upgrade(el);
  }
}

/** @param {unknown} result */
async function handleShadowBasketSync(result) {
  clearDeferGiftReconcileFallback();
  deferGiftReconcileUntilShadowSync = false;
  await finishDeferredGiftReconcile();
}

if (!customElements.get('cart-gift-voucher-component')) {
  setupGiftVoucherCheckoutGuard();
  customElements.define('cart-gift-voucher-component', CartGiftVoucherComponent);
  upgradeGiftVoucherComponents();
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.querySelector('[data-gift-card]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const host = form.closest('cart-gift-voucher-component');
      if (host instanceof CartGiftVoucherComponent) void host.submitVoucher();
    },
    true
  );
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    requestAnimationFrame(() => upgradeGiftVoucherComponents());
    event.promise
      ?.then(async ({ cart, detail }) => {
        const itemCount = cartItemCountFromLinesUpdate(
          /** @type {Record<string, unknown> | undefined} */ (cart),
          detail
        );
        if (itemCount !== null && itemCount <= 0) {
          await clearGiftVouchersFromShopifyCart();
        }
      })
      .catch(() => {});
  });
  document.addEventListener(StandardEvents.cartDiscountUpdate, () => {
    requestAnimationFrame(() => upgradeGiftVoucherComponents());
  });
  window.addEventListener('peliqan:shadow-basket:sync', (event) => {
    void handleShadowBasketSync(event instanceof CustomEvent ? event.detail : undefined);
  });
}